import { usePermissao } from '@/utils/permissao';
import { AnexosPanel } from '@/components/anexos/AnexosPanel';
import { Building2, FileText, History, MessageCircle, Pencil, Plus, TriangleAlert, UserRound, Users } from 'lucide-react';
import { AvisarClienteDialog } from './AvisarClienteDialog';
import React, { ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { FichaService, SinistroService } from '../../services';
import { Seguro } from '@/types/Seguro';
import { SinistroDTO } from '@/types/SinistroDTO';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge, EntityIcon, IconeTile, TIPO_SEGURO_CORES } from '@/components/ui/cores';
import { PageHeader } from '@/components/ui/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import FormattedDate from '@/components/ui/formatted-date';
import { getTipoEndossoLabel } from '@/types/enum/TipoEndosso';
import { getStatusSinistroLabel, StatusSinistroEnum } from '@/types/enum/StatusSinistro';
import { getTipoSeguroLabel, TipoSeguroEnum } from '@/types/enum/TipoSeguro';
import { formatCpfCnpj, formatCurrency, formatPercent } from '@/utils/format';
import { getTenant } from '@/utils/TenantUtils';
import { ErrorPage } from '../ErrorPage';

const Campo = ({ titulo, children, className }: { titulo: string; children?: ReactNode; className?: string }) => (
    <div className={className}>
        <dt className="text-xs text-muted-foreground">{titulo}</dt>
        <dd className="mt-0.5 whitespace-pre-line break-words text-sm">{children || <span className="text-muted-foreground/60">—</span>}</dd>
    </div>
);

const Painel = ({ titulo, icon, cor, acao, children }: { titulo: string; icon: ReactNode; cor: string; acao?: ReactNode; children: ReactNode }) => (
    <Card className="p-5 shadow-none">
        <h2 className="mb-4 flex items-center gap-2.5 text-base font-semibold">
            <IconeTile size="sm" cor={cor}>{icon}</IconeTile>
            {titulo}
            {acao && <span className="ml-auto">{acao}</span>}
        </h2>
        {children}
    </Card>
);

// Lista de contatos (telefones, e-mails) no formato "descrição: valor"
const Contatos = ({ itens }: { itens: { id?: string; descricao: string; valor: string }[] }) =>
    itens.length === 0 ? null : (
        <ul className="space-y-1 text-sm">
            {itens.map((item, i) => (
                <li key={item.id || i}><span className="text-muted-foreground">{item.descricao}:</span> {item.valor}</li>
            ))}
        </ul>
    );

const SeguroViewPage: React.FC = () => {
    const podeAlterar = usePermissao('SEGURO_WRITE');
    const [ficha, setFicha] = useState<Seguro>();
    const [sinistros, setSinistros] = useState<SinistroDTO[]>([]);
    const [renovacao, setRenovacao] = useState<Seguro>();
    const [avisando, setAvisando] = useState(false);
    const [error, setError] = useState<AxiosError>();
    const navigate = useNavigate();
    const { codigo } = useParams();
    const tenant = getTenant();

    useEffect(() => {
        if (!codigo || codigo === 'novo') return;
        FichaService.getByCodigo(codigo)
            .then(({ data }) => {
                setFicha(data);
                FichaService.findFichas(`seguroAnterior==${data.id}`).then(r => setRenovacao(r.data.content[0])).catch(setError);
                SinistroService.getByFicha(data.id!).then(r => setSinistros(r.data)).catch(setError);
            })
            .catch(setError);
    }, [codigo]);

    if (error) {
        return <ErrorPage axiosError={error} />
    }

    if (!ficha) {
        return <Skeleton className="h-96 w-full" />
    }

    const { segurado, seguradora, corretor } = ficha;
    const comissao = ficha.premioLiquido != null && ficha.percentualComissao != null
        ? `${formatCurrency(ficha.premioLiquido * ficha.percentualComissao / 100)} (${formatPercent(ficha.percentualComissao)})`
        : undefined;

    return (
        <div className="space-y-6">
            <PageHeader icon={<EntityIcon entidade="seguro" />} title={segurado?.nome || 'Seguro'}>
                {!ficha.renovado && !renovacao && (
                    <Button variant="outline" onClick={() => setAvisando(true)}>
                        <MessageCircle className="mr-2 h-4 w-4" /> Avisar cliente
                    </Button>
                )}
                {podeAlterar && (
                    <>
                    <Button variant="outline" onClick={() => navigate(`/${tenant}/seguros/${codigo}/sinistro/novo`)}>
                        <Plus className="mr-2 h-4 w-4" /> Novo sinistro
                    </Button>
                    <Button onClick={() => navigate(`/${tenant}/seguros/${codigo}`)}>
                        <Pencil className="mr-2 h-4 w-4" /> Editar
                    </Button>
                    </>
                )}
            </PageHeader>
            <AvisarClienteDialog seguro={avisando ? ficha : undefined} onClose={() => setAvisando(false)} />

            {(ficha.seguroAnterior || renovacao || ficha.renovado) && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
                    <History className="h-4 w-4 text-muted-foreground" />
                    {ficha.seguroAnterior && (
                        <Link className="font-medium text-primary hover:underline" to={`/${tenant}/seguros/${ficha.seguroAnterior}/resumo`}>Ver seguro anterior</Link>
                    )}
                    {renovacao && (
                        <span>Renovado em <Link className="font-medium text-primary hover:underline" to={`/${tenant}/seguros/${renovacao.id}/resumo`}>
                            novo seguro até <FormattedDate value={renovacao.vigenciaFinal} />
                        </Link></span>
                    )}
                    {ficha.renovado && !renovacao && <span>Marcado como renovado</span>}
                </div>
            )}

            <div className="grid items-start gap-6 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                    <Painel titulo="Seguro" icon={<FileText />} cor="bg-blue-500/10 text-blue-600 dark:text-blue-400">
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                            <Campo titulo="Tipo">
                                {ficha.tipoSeguro && <Badge className={TIPO_SEGURO_CORES[ficha.tipoSeguro]?.badge}>{getTipoSeguroLabel(ficha.tipoSeguro)}</Badge>}
                            </Campo>
                            <Campo titulo="Vigência">
                                {ficha.vigenciaInicial && <><FormattedDate value={ficha.vigenciaInicial} /> a <FormattedDate value={ficha.vigenciaFinal} /></>}
                            </Campo>
                            <Campo titulo="Prêmio líquido">{ficha.premioLiquido != null && formatCurrency(ficha.premioLiquido)}</Campo>
                            <Campo titulo="Comissão">{comissao}</Campo>
                            <Campo titulo="Descrição" className="col-span-2 sm:col-span-4">{ficha.descricao}</Campo>
                            {ficha.tipoSeguro === TipoSeguroEnum.veicular && (
                                <>
                                <Campo titulo="Placa">{ficha.placa}</Campo>
                                <Campo titulo="Chassi" className="sm:col-span-3">{ficha.chassi}</Campo>
                                </>
                            )}
                            <Campo titulo="Observação" className="col-span-2 sm:col-span-4">{ficha.observacao}</Campo>
                        </dl>
                        {!!ficha.endossos?.length && (
                            <div className="mt-5 border-t pt-4">
                                <h3 className="mb-2 text-sm font-medium">Endossos</h3>
                                <ul className="space-y-2">
                                    {ficha.endossos.map((endosso, i) => (
                                        <li key={endosso.id || i} className="rounded-md bg-muted/40 px-3 py-2 text-sm">
                                            <p className="font-medium">{getTipoEndossoLabel(endosso.tipo)}</p>
                                            {endosso.observacaoEndosso && <p className="mt-0.5 whitespace-pre-line text-muted-foreground">{endosso.observacaoEndosso}</p>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </Painel>

                    <Painel titulo="Segurado" icon={<Users />} cor="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                        acao={segurado?.id && <Link to={`/${tenant}/segurado/${segurado.id}`} className="text-sm font-normal text-primary hover:underline">Abrir</Link>}>
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                            <Campo titulo="Nome" className="col-span-2">{segurado?.nome}</Campo>
                            <Campo titulo={segurado?.tipoPessoa === 'JURIDICA' ? 'CNPJ' : 'CPF'}>{segurado?.cpfCnpj && formatCpfCnpj(segurado.cpfCnpj)}</Campo>
                            <Campo titulo={segurado?.tipoPessoa === 'JURIDICA' ? 'Inscrição estadual' : 'RG'}>{segurado?.rgIe}</Campo>
                            {!!segurado?.telefones?.length && (
                                <Campo titulo="Telefones" className="col-span-2"><Contatos itens={segurado.telefones.map(t => ({ descricao: t.descricao, valor: t.telefone }))} /></Campo>
                            )}
                            {!!segurado?.emails?.length && (
                                <Campo titulo="E-mails" className="col-span-2"><Contatos itens={segurado.emails.map(e => ({ descricao: e.descricao, valor: e.email }))} /></Campo>
                            )}
                            {segurado?.enderecos?.map((endereco, i) => (
                                <Campo key={endereco.id || i} titulo={segurado.enderecos!.length > 1 ? `Endereço ${i + 1}` : 'Endereço'} className="col-span-2 sm:col-span-4">
                                    {[`${endereco.logradouro}, ${endereco.numero}`, endereco.complemento, endereco.bairro, `${endereco.cidade}-${endereco.uf}`, endereco.cep].filter(Boolean).join(', ')}
                                </Campo>
                            ))}
                        </dl>
                    </Painel>

                    <Painel titulo="Sinistros" icon={<TriangleAlert />} cor="bg-rose-500/10 text-rose-600 dark:text-rose-400">
                        {sinistros.length === 0 ? (
                            <p className="text-sm text-muted-foreground">Nenhum sinistro registrado para este seguro.</p>
                        ) : (
                            <ul className="-mx-5 -mb-5 divide-y border-t">
                                {sinistros.map(sin => (
                                    <li key={sin.id}>
                                        <Link to={`/${tenant}/seguros/${codigo}/sinistro/${sin.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 text-sm hover:bg-muted/50">
                                            <FormattedDate value={sin.data} />
                                            <span className="text-muted-foreground">{sin.aviso ? `Aviso ${sin.aviso}` : 'Sem número de aviso'}</span>
                                            <span className="text-muted-foreground">{sin.ocorrencias?.length || 0} {sin.ocorrencias?.length === 1 ? 'ocorrência' : 'ocorrências'}</span>
                                            <Badge className={`ml-auto ${sin.status === StatusSinistroEnum.pendente ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'}`}>
                                                {getStatusSinistroLabel(sin.status!)}
                                            </Badge>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Painel>
                </div>

                <div className="space-y-6">
                    <Painel titulo="Corretor" icon={<UserRound />} cor="bg-violet-500/10 text-violet-600 dark:text-violet-400">
                        <dl className="space-y-3">
                            <Campo titulo="Nome">{corretor?.nome}</Campo>
                            <Campo titulo="E-mail">{corretor?.email}</Campo>
                        </dl>
                    </Painel>

                    <Painel titulo="Seguradora" icon={<Building2 />} cor="bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        <dl className="space-y-3">
                            <Campo titulo="Nome">{seguradora?.nome}</Campo>
                            {!!seguradora?.telefones?.length && (
                                <Campo titulo="Telefones"><Contatos itens={seguradora.telefones.map(t => ({ descricao: t.descricao, valor: t.telefone }))} /></Campo>
                            )}
                            {!!seguradora?.emails?.length && (
                                <Campo titulo="E-mails"><Contatos itens={seguradora.emails.map(e => ({ descricao: e.descricao, valor: e.email }))} /></Campo>
                            )}
                        </dl>
                    </Painel>

                    <AnexosPanel entidade="seguro" registro={ficha.id} somenteLeitura />
                </div>
            </div>
        </div>
    );
}

export default SeguroViewPage;
